import { Link } from 'react-router-dom';
import { useCatalog, groupByTier } from '../lib/catalog.js';
import type { Sku } from '@z9-store/core';

export function CatalogPage(): JSX.Element {
  const q = useCatalog();
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  const skus = q.data ?? [];
  if (skus.length === 0) return <EmptyState />;
  const groups = groupByTier(skus);
  return (
    <div data-testid="catalog" className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Z9 Security Shop</h1>
        <p className="mt-2 text-sm text-slate-600">
          Pick a product to see the details. Buying is gated behind sign-in.
        </p>
      </header>
      {groups.map(({ tier, skus }) => (
        <section key={tier}>
          <h2 className="text-sm font-medium uppercase tracking-wider text-slate-500">{tier}</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {skus.map((sku) => (
              <SkuCard key={sku.skuId} sku={sku} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SkuCard({ sku }: { sku: Sku }): JSX.Element {
  return (
    <Link
      to={`/product/${encodeURIComponent(sku.skuId)}`}
      data-testid={`catalog-product-${sku.skuId}`}
      className="block rounded-lg border border-slate-200 bg-white p-4 transition hover:border-slate-400 hover:shadow-sm"
    >
      <div className="text-base font-semibold text-slate-900">{sku.displayName}</div>
      <p className="mt-2 line-clamp-3 text-sm text-slate-600">{sku.description}</p>
      <div className="mt-3 text-xs font-medium uppercase tracking-wider text-slate-400">
        View details &rarr;
      </div>
    </Link>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="space-y-4 text-sm text-slate-500" data-testid="catalog-loading">
      <p>Loading...</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded bg-red-50 p-4 text-sm text-red-700" data-testid="catalog-error">
      Could not load the catalog: {message}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded bg-slate-100 p-6 text-sm text-slate-700" data-testid="catalog-empty">
      No products are published yet.
    </div>
  );
}
